import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { RovoTemplatesCallout } from '@atlassian/conversation-assistant-blank-object-experience/rovo-templates-callout';

import {
	IN_EDITOR_TEMPLATES_UPDATE_CONTENT_EXPERIENCE,
	IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { stopExperienceOnError } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { expValEquals } from '@confluence/feature-experiments';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useCreateContentFromTemplate } from '@confluence/create-content-from-template/entry-points/useCreateContentFromTemplate.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { Template } from '@confluence/template-list/entry-points/TemplateListQuery.ts';
import { TemplateList } from '@confluence/template-list/entry-points/TemplateListLoader.ts';
import { getErrorMessage } from '@confluence/template-utils/entry-points/handleErrorMessages.ts';
import {
	isBadThirdPartyBlueprintError,
	isBadEditedFirstPartyBlueprintError,
} from '@confluence/template-utils/entry-points/errorUtils.ts';
import {
	getTemplateKey,
	getTemplateIdAttributesForAnalytics,
	getSearchQueryMatches,
} from '@confluence/template-utils/entry-points/templateUtils.ts';
import { removeInEditorTemplatesQueryParam } from '@confluence/template-utils/entry-points/urlUtils.ts';
import { CreateDialogContext } from '@confluence/create-dialog-context';
import { containsNetworkError } from '@confluence/network';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';

import {
	collectCreateContentExperiences,
	collectUpdateContentExperiences,
} from './experienceTracking.ts';
import { OverwriteWarningModal } from './OverwriteWarningModal.tsx';
import { AlwaysDiscardChangesModal } from './AlwaysDiscardChangesModal.tsx';
import { SearchBar } from './SearchBar.tsx';
import { TemplatePanelSpaceSelector } from './TemplatePanelSpaceSelector.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PanelWrapper = styled.div({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InputFieldsWrapper = styled.div({
	padding: `0 ${token('space.050')}`,
});

const templateListWrapperStyles = xcss({
	marginTop: 'space.200',
});

type TemplatePanelProps = {
	parentPageId: string;
	currentSpaceKey: string;
	contentId: string;
	isPanelDisabled?: boolean;
	onContentUpdated?: () => void;
	beforeTemplateSelect?: () => void;
	beforeUpdateRequest?: () => void;
	onError?: () => void;
	hasUserAddedContent: boolean | null;
	title: string;
	templateAllowList?: string[];
	isAlwaysDiscardChanges?: boolean;
	isSpacePickerDisabled?: boolean;
};

export enum ErrorCodes {
	VALIDATION_ERROR = 400,
	AUTHORIZATION_ERROR = 401,
	NOT_FOUND_ERROR = 404,
	CONTENT_ERROR = 500,
}

type InjectedFlagsProps = {
	flags: FlagsStateContainer;
};

type InjectedTemplatePanelProps = InjectedFlagsProps & TemplatePanelProps;

export const TemplatePanel: FC<TemplatePanelProps> = withFlags(
	({
		isPanelDisabled = false,
		parentPageId,
		currentSpaceKey,
		contentId,
		onContentUpdated,
		beforeUpdateRequest,
		beforeTemplateSelect,
		hasUserAddedContent,
		title,
		flags,
		onError,
		templateAllowList,
		isAlwaysDiscardChanges,
		isSpacePickerDisabled,
	}: InjectedTemplatePanelProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const experienceTracker = useContext(ExperienceTrackerContext);
		const intl = useIntl();
		const { userId: currentUserId, isRovoLLMEnabled } = useSessionData();
		const routeDataRef = useRouteDataRef();
		const isCurrentPageLive = useIsCurrentPageLive();
		const { mode, toggleConversationAssistant, isConversationAssistantSidebarActive } =
			useToggleConversationAssistant();
		const { setQueryParams } = useContext(RoutesContext);

		const { showBlueprintVariableWizard, showTemplateVariableInputDrawer } =
			useContext(CreateDialogContext);

		const createFromTemplate = useCreateContentFromTemplate();
		const [selectedSpaceKey, setSelectedSpaceKey] = useState(currentSpaceKey);
		const [searchQuery, setSearchQuery] = useState('');
		const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
		const [isDisabled, setIsDisabled] = useState(isPanelDisabled);
		const [isCreating, setIsCreating] = useState(false);

		const onCreateWithRovoCallback = useCallback(() => {
			removeInEditorTemplatesQueryParam(setQueryParams, isRovoLLMEnabled);
			// Only toggle if CWR is not already open (in sidebar or mini-modal mode)
			if (
				!isConversationAssistantSidebarActive &&
				mode !== ConversationAssistantUIModes.MINI_MODAL
			) {
				toggleConversationAssistant('mini-modal');
			}
		}, [
			setQueryParams,
			toggleConversationAssistant,
			isConversationAssistantSidebarActive,
			mode,
			isRovoLLMEnabled,
		]);

		// These variables needed for analytic events.
		// No need to put them into `state`, since they are not
		// rendered
		const hasSpaceChanged = useRef(false);

		useEffect(() => {
			if (
				showTemplateVariableInputDrawer ||
				showBlueprintVariableWizard ||
				isPanelDisabled ||
				isCreating
			) {
				setIsDisabled(true);
			} else {
				setIsDisabled(false);
				// @Todo CTE-337 reset value of stored template until dialog close callback is made
				setSelectedTemplateKey('');
			}
		}, [isCreating, showTemplateVariableInputDrawer, showBlueprintVariableWizard, isPanelDisabled]);

		const handleError = (e) => {
			onError && onError();
			const errorCode = e?.graphQLErrors?.[0]?.originalError?.response?.status;

			const isNotFoundGraphqlError = isNotFoundError(e);
			const isThirdPartyBlueprintError = isBadThirdPartyBlueprintError(e);
			const isEditedFirstPartyBlueprintError = isBadEditedFirstPartyBlueprintError(e);
			const isNoNetworkError = containsNetworkError(e);

			void flags.showErrorFlag(
				getErrorMessage({
					errorCode,
					isThirdPartyBlueprintError,
					isEditedFirstPartyBlueprintError,
					isLivePage: isCurrentPageLive,
				}),
			);

			if (
				isThirdPartyBlueprintError ||
				isEditedFirstPartyBlueprintError ||
				isNotFoundGraphqlError ||
				isNoNetworkError
			) {
				experienceTracker.succeed({
					name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_EXPERIENCE,
				});
				experienceTracker.abort({
					name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
					reason: isThirdPartyBlueprintError
						? 'third party blueprint error'
						: isNotFoundGraphqlError
							? 'not found graphql error'
							: isNoNetworkError
								? 'no network error'
								: 'edited first party blueprint error',
				});
				if (isNoNetworkError) {
					markErrorAsHandled(e);
				}
			} else {
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_MODES,
				});

				stopExperienceOnError(IN_EDITOR_TEMPLATES_UPDATE_CONTENT_EXPERIENCE, e);
				stopExperienceOnError(IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE, e);
			}

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'templateDrawer',
					action: 'error',
					actionSubject: 'editPageScreen',
					actionSubjectId: 'contentCreateWithTemplateError',
					attributes: {
						errorMessage: e.message,
					},
				},
			}).fire();
		};

		const handleSpaceChange = useCallback(
			({ spaceKey: newSpaceKey }: any): void => {
				setSelectedSpaceKey(newSpaceKey);
			},
			[setSelectedSpaceKey],
		);

		const handleSearchChange = useCallback(
			(searchQuery: string): void => {
				setSearchQuery(searchQuery);
			},
			[setSearchQuery],
		);

		const handleSearchFocus = () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'templateDrawer',
					action: 'clicked',
					actionSubject: 'textField',
					actionSubjectId: 'templateSearchBar',
				},
			}).fire();
		};

		const closeDraftDiscardModal = () => {
			// todo fire analytics event https://hello.atlassian.net/browse/CTE-677
		};

		const handleTemplateSelectWithModal =
			(dialogs: DialogsStateContainer) =>
			(template: Template, additionalStatAttributes?: object): void => {
				setSelectedTemplateKey(getTemplateKey(template));
				beforeTemplateSelect && beforeTemplateSelect();

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'templateDrawer',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'templateOption',
						attributes: {
							currentRouteName: routeDataRef?.current?.routeName,
							currentRouteIsLive: isCurrentPageLive,
							searchQueryPresent: !!searchQuery.length,
							spaceChanged: selectedSpaceKey !== currentSpaceKey,
							...getTemplateIdAttributesForAnalytics(template),
							...additionalStatAttributes,
							searchQueryMatches: getSearchQueryMatches(template, intl, currentUserId, searchQuery),
						},
					},
				}).fire();
				hasSpaceChanged.current = false;

				if (hasUserAddedContent) {
					const modal = isAlwaysDiscardChanges ? AlwaysDiscardChangesModal : OverwriteWarningModal;

					dialogs.showDialog(modal, {
						onCreate: handleTemplateSelect,
						onClose: closeDraftDiscardModal,
						template,
					});
				} else {
					handleTemplateSelect(template);
				}
			};

		const handleTemplateSelect = (template: Template, replaceContent: boolean = true): void => {
			setIsCreating(true);
			const shouldInjectContent = replaceContent && contentId;

			experienceTracker.start({
				name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_EXPERIENCE,
				collect: shouldInjectContent
					? collectUpdateContentExperiences
					: collectCreateContentExperiences,
			});
			experienceTracker.start({
				name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
				timeout: ExperienceTimeout.IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR,
			});

			const options = {
				...(onContentUpdated && { onContentUpdated }),
				onVariableInputError: handleError,
				beforeUpdateRequest,
				templateOriginSpaceKey: selectedSpaceKey,
				parentPageId,
				...(shouldInjectContent && { contentId }),
				...(shouldInjectContent && title && { title }),
				...(isCurrentPageLive && { subType: 'live' }),
			};

			createFromTemplate(template, currentSpaceKey, options)
				.then(() => {
					setIsCreating(false);
				})
				.catch((e) => {
					setIsCreating(false);
					handleError(e);
				});
		};

		return (
			<ErrorBoundary attribution={Attribution.PAGE_MODES}>
				<Subscribe to={[DialogsStateContainer]}>
					{(dialogs: DialogsStateContainer) => (
						<PanelWrapper data-testid="template-panel">
							<InputFieldsWrapper>
								{!isSpacePickerDisabled && (
									<TemplatePanelSpaceSelector
										onChange={handleSpaceChange}
										spaceKey={currentSpaceKey}
										isDisabled={isDisabled}
									/>
								)}
								<SearchBar
									onChange={handleSearchChange}
									onFocus={handleSearchFocus}
									isDisabled={isDisabled}
								/>
							</InputFieldsWrapper>
							{isRovoLLMEnabled && expValEquals('cwr_templates_callout', 'isEnabled', true) && (
								<RovoTemplatesCallout
									onCreateWithRovo={onCreateWithRovoCallback}
									contentType={isCurrentPageLive ? 'liveDoc' : 'page'}
								/>
							)}
							<Box xcss={templateListWrapperStyles}>
								<TemplateList
									onTemplateSelect={handleTemplateSelectWithModal(dialogs)}
									spaceKey={selectedSpaceKey}
									templateBlueprintAllowList={templateAllowList}
									currentSpaceKey={currentSpaceKey}
									searchQuery={searchQuery}
									selectedTemplateKey={selectedTemplateKey}
									isDisabled={isDisabled}
								/>
							</Box>
						</PanelWrapper>
					)}
				</Subscribe>
			</ErrorBoundary>
		);
	},
) as any;
